import React, { useMemo, useState, useEffect } from 'react';
import {
    Autocomplete, Box, Button, CircularProgress, Grid, IconButton, Stack, TextField, Typography, Chip, Card,
    Accordion, AccordionSummary, AccordionDetails, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import {
    Send as SendIcon, Draw as DrawIcon, Clear as ClearIcon,
    ExpandMore as ExpandMoreIcon, PersonSearch as PersonSearchIcon,
    Mic as MicIcon, Stop as StopIcon,
    Visibility as VisibilityIcon, Download as DownloadIcon
} from '@mui/icons-material';
import { GAS_READINGS } from './constants';
import useDictation from '../../../hooks/useDictation';
import useSftpDownload from './useSftpDownload';
import PdfPreviewModal from '../../jha/pdf/PdfPreviewModal';
import axiosInstance from '../../../axiosInstance';

const accordionSx = { mb: 0.5, '&.Mui-expanded': { margin: '0 0 4px 0' } };
const accordionSummarySx = { minHeight: '40px !important', '& .MuiAccordionSummary-content': { margin: '6px 0 !important' } };
const sectionTitle = (text) => <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>{text}</Typography>;

export default function BumpTestForm({
    form, errors, isReadOnly, saving,
    sortedJobs, filteredTools,
    onFieldChange, onJobChange, onToolChange, onGasResult,
    onOpenSignature, onClearSignature, onOpenEmployeeModal,
    onSubmit
}) {
    const { docLoading, handleDownload } = useSftpDownload();
    const [pdfPreviewOpen, setPdfPreviewOpen] = React.useState(false);
    const [pdfBlob, setPdfBlob] = React.useState(null);
    const [pdfFilename, setPdfFilename] = React.useState('');
    const [pdfLoading, setPdfLoading] = React.useState(false);

    // Tool status from API: today's bump tests + last 3 calibrations
    const [todayBumpTests, setTodayBumpTests] = useState([]);
    const [toolCalibrations, setToolCalibrations] = useState([]);
    const [statusLoading, setStatusLoading] = useState(false);
    useEffect(() => {
        if (!form.toolPart) { setTodayBumpTests([]); setToolCalibrations([]); return; }
        let cancelled = false;
        (async () => {
            setStatusLoading(true);
            try {
                const { data } = await axiosInstance.get(`/gas-monitor-test/tool-status?part=${encodeURIComponent(form.toolPart)}`);
                if (cancelled) return;
                setTodayBumpTests(data.todayBumpTests || []);
                setToolCalibrations(data.calibrations || []);
            } catch (e) {
                console.error('Failed to load tool status:', e);
            } finally {
                if (!cancelled) setStatusLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [form.toolPart]);

    const handleViewPdf = async (sftpPath, docId) => {
        setPdfLoading(true);
        setPdfPreviewOpen(true);
        setPdfFilename(sftpPath.split(/[/\\]/).pop() || 'certificate.pdf');
        const blob = await handleDownload(sftpPath, docId, 'blob');
        if (blob) setPdfBlob(blob);
        setPdfLoading(false);
    };

    const {
        isSupported: dictationSupported,
        isListening,
        start: startDictation,
        stop: stopDictation
    } = useDictation({
        lang: 'en-US',
        interimResults: false,
        continuous: false,
        safetyTimeoutMs: 15000,
        prime: true,
    });

    const toggleCommentsDictation = async () => {
        if (!dictationSupported) return;
        if (isListening) {
            stopDictation();
        } else {
            await startDictation({
                onResult: (transcript) => {
                    if (!transcript) return;
                    const current = form.comments || '';
                    const needsSpace = current && !current.endsWith(' ');
                    onFieldChange('comments', current + (needsSpace ? ' ' : '') + transcript);
                },
            });
        }
    };

    return (
        <>
            {/* Job */}
            <Accordion defaultExpanded sx={accordionSx}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>{sectionTitle('Job Information')}</AccordionSummary>
                <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                    <Grid container rowSpacing={1} columnSpacing={1}>
                        <Grid item xs={8} sm={9}>
                            <Autocomplete options={sortedJobs} getOptionLabel={o => o.label || ''} disabled={isReadOnly}
                                value={sortedJobs.find(j => j.Job === form.jobCode) || null} onChange={onJobChange}
                                isOptionEqualToValue={(o, v) => o.Job === v.Job}
                                renderInput={p => <TextField {...p} label="Job *" size="small" error={!!errors.jobCode} helperText={errors.jobCode} />} />
                        </Grid>
                        <Grid item xs={4} sm={3}>
                            <TextField label="Date" type="date" size="small" fullWidth value={form.testDate}
                                onChange={e => onFieldChange('testDate', e.target.value)} disabled={isReadOnly} InputLabelProps={{ shrink: true }} />
                        </Grid>
                        <Grid item xs={6}><TextField label="Customer" size="small" fullWidth value={form.customerName} disabled InputLabelProps={{ shrink: true }} /></Grid>
                        <Grid item xs={6}><TextField label="Location" size="small" fullWidth value={form.jobLocation} disabled InputLabelProps={{ shrink: true }} /></Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>

            {/* Tool */}
            <Accordion defaultExpanded sx={accordionSx}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>{sectionTitle('Tool')}</AccordionSummary>
                <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                    <Grid container rowSpacing={1} columnSpacing={1}>
                        <Grid item xs={12}>
                            <Autocomplete options={filteredTools} getOptionLabel={o => o.label || ''} disabled={isReadOnly}
                                value={filteredTools.find(t => t.part === form.toolPart && t.serialNo === form.toolSerialNo) || null}
                                onChange={onToolChange} isOptionEqualToValue={(o, v) => o.part === v.part && o.serialNo === v.serialNo}
                                renderInput={p => <TextField {...p} label="Select Tool *" size="small" error={!!errors.toolPart} helperText={errors.toolPart} />} />
                        </Grid>
                        {form.toolPart && <>
                            <Grid item xs={6} sm={3}><TextField label="Part" size="small" fullWidth value={form.toolPart} disabled InputLabelProps={{ shrink: true }} /></Grid>
                            <Grid item xs={6} sm={3}><TextField label="Serial No" size="small" fullWidth value={form.toolSerialNo} disabled InputLabelProps={{ shrink: true }} /></Grid>
                            <Grid item xs={6} sm={3}><TextField label="Model" size="small" fullWidth value={form.toolModel} disabled InputLabelProps={{ shrink: true }} /></Grid>
                            <Grid item xs={6} sm={3}><TextField label="Manufacturer" size="small" fullWidth value={form.toolManufacturer} disabled InputLabelProps={{ shrink: true }} /></Grid>
                        </>}
                    </Grid>
                </AccordionDetails>
            </Accordion>

            {/* Today's Bump Tests */}
            {form.toolPart && (
                <Accordion defaultExpanded sx={accordionSx}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
                        {sectionTitle(`Today's Bump Tests (${todayBumpTests.length})`)}
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                        {statusLoading ? (
                            <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={20} /></Box>
                        ) : todayBumpTests.length > 0 ? (
                            <Stack spacing={0.5}>
                                {todayBumpTests.map((bt) => (
                                    <Card key={bt.id} variant="outlined" sx={{ p: 1.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="body2" fontWeight={600}>Bump Test</Typography>
                                            <Chip label={bt.overallResult === 'pass' ? 'PASS' : 'FAIL'} size="small"
                                                sx={{ height: 22, fontWeight: 700, backgroundColor: bt.overallResult === 'pass' ? '#4caf50' : '#f44336', color: '#fff' }} />
                                        </Box>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.5, mb: 1 }}>
                                            {['h2s', 'co', 'o2', 'lel'].map(gas => {
                                                const val = bt[`${gas}Result`];
                                                return (
                                                    <Box key={gas} sx={{ textAlign: 'center', p: 0.5, borderRadius: 1, backgroundColor: '#f9fafb' }}>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', textTransform: 'uppercase' }}>{gas}</Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 700, color: val === 'pass' ? '#2e7d32' : val === 'fail' ? '#c62828' : '#999' }}>
                                                            {val || '—'}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                        <Typography variant="caption" color="text.secondary">
                                            {bt.employeeName}{bt.jobCode ? ` · ${bt.jobCode}` : ''}{bt.submittedAt ? ` · ${bt.submittedAt}` : ''}
                                        </Typography>
                                    </Card>
                                ))}
                            </Stack>
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 1 }}>
                                No bump tests recorded today
                            </Typography>
                        )}
                    </AccordionDetails>
                </Accordion>
            )}

            {/* Last Calibrations */}
            {form.toolPart && (
                <Accordion sx={accordionSx}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
                        {sectionTitle(`Last Calibrations (${toolCalibrations.length})`)}
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                        {statusLoading ? (
                            <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={20} /></Box>
                        ) : toolCalibrations.length > 0 ? (
                            <Stack spacing={0.5}>
                                {toolCalibrations.map((cal, idx) => {
                                    const isOverdue = cal.calNextDate && new Date(cal.calNextDate) < new Date();
                                    const hasDoc = !!cal.calDoc;
                                    const docName = hasDoc ? cal.calDoc.split(/[/\\]/).pop() : null;
                                    const isPdf = docName?.toLowerCase().endsWith('.pdf');
                                    return (
                                        <Card key={idx} variant="outlined" sx={{ p: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <Box>
                                                    <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
                                                        {cal.calDate || '—'}
                                                        {isOverdue && <Chip label="Overdue" size="small" sx={{ ml: 1, height: 18, fontSize: '0.6rem', backgroundColor: '#f44336', color: '#fff' }} />}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {cal.calWho ? `By: ${cal.calWho}` : ''}{cal.calNextDate ? ` · Next: ${cal.calNextDate}` : ''}
                                                    </Typography>
                                                </Box>
                                                {hasDoc && (
                                                    <Stack direction="row" spacing={0.5}>
                                                        {isPdf && (
                                                            <IconButton size="small" color="primary" disabled={docLoading === `cal-${idx}`}
                                                                onClick={() => handleViewPdf(cal.calDoc, `cal-${idx}`)}>
                                                                {docLoading === `cal-${idx}` ? <CircularProgress size={14} /> : <VisibilityIcon sx={{ fontSize: 16 }} />}
                                                            </IconButton>
                                                        )}
                                                        <IconButton size="small" disabled={docLoading === `cal-${idx}`}
                                                            onClick={() => handleDownload(cal.calDoc, `cal-${idx}`, 'download')}>
                                                            <DownloadIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    </Stack>
                                                )}
                                            </Box>
                                        </Card>
                                    );
                                })}
                            </Stack>
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 1 }}>
                                No calibration records
                            </Typography>
                        )}
                    </AccordionDetails>
                </Accordion>
            )}

            {/* Test Results */}
            <Accordion defaultExpanded sx={accordionSx}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>{sectionTitle('Test Results')}</AccordionSummary>
                <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                    <Stack spacing={1.5}>
                        {GAS_READINGS.map(gas => (
                            <Box key={gas.key} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography variant="body2" sx={{ minWidth: 60, fontWeight: 600 }}>{gas.label}</Typography>
                                <ToggleButtonGroup value={form[gas.key]} exclusive size="small" disabled={isReadOnly}
                                    onChange={(_, v) => onGasResult(gas.key, v)}>
                                    <ToggleButton value="pass" sx={{ px: 3, '&.Mui-selected': { backgroundColor: '#4caf50', color: '#fff', '&:hover': { backgroundColor: '#43a047' } } }}>PASS</ToggleButton>
                                    <ToggleButton value="fail" sx={{ px: 3, '&.Mui-selected': { backgroundColor: '#f44336', color: '#fff', '&:hover': { backgroundColor: '#e53935' } } }}>FAIL</ToggleButton>
                                </ToggleButtonGroup>
                                {errors[gas.key] && <Typography variant="caption" color="error">{errors[gas.key]}</Typography>}
                            </Box>
                        ))}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pt: 1, borderTop: '1px solid #e0e0e0' }}>
                            <Typography variant="body2" sx={{ minWidth: 60, fontWeight: 700 }}>Overall</Typography>
                            {form.overallResult ? (
                                <Chip label={form.overallResult === 'pass' ? 'PASS' : 'FAIL'}
                                    sx={{ backgroundColor: form.overallResult === 'pass' ? '#4caf50' : '#f44336', color: '#fff', fontWeight: 700 }} />
                            ) : <Typography variant="body2" color="text.secondary">Select all readings</Typography>}
                        </Box>
                    </Stack>
                </AccordionDetails>
            </Accordion>

            {/* Employee & Signature */}
            <Accordion defaultExpanded sx={accordionSx}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>{sectionTitle('Employee & Signature')}</AccordionSummary>
                <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                    <Grid container rowSpacing={1} columnSpacing={1}>
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TextField label="Employee *" size="small" fullWidth value={form.employeeName} disabled
                                    InputLabelProps={{ shrink: true }} error={!!errors.employeeName} helperText={errors.employeeName} />
                                {!isReadOnly && <IconButton onClick={onOpenEmployeeModal} color="primary" size="small"><PersonSearchIcon /></IconButton>}
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            {form.signature ? (
                                <Card variant="outlined" sx={{ p: 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary">Signed: {form.signatureDate}</Typography>
                                        {!isReadOnly && <IconButton size="small" onClick={onClearSignature} color="error"><ClearIcon fontSize="small" /></IconButton>}
                                    </Box>
                                    <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 0.5, backgroundColor: '#fff' }}>
                                        <img src={form.signature} alt="Signature" style={{ width: '100%', maxHeight: 100, objectFit: 'contain' }} />
                                    </Box>
                                </Card>
                            ) : (
                                <Button variant="outlined" startIcon={<DrawIcon />} onClick={onOpenSignature}
                                    disabled={isReadOnly} fullWidth color={errors.signature ? 'error' : 'primary'}>Add Signature</Button>
                            )}
                            {errors.signature && !form.signature && <Typography variant="caption" color="error">{errors.signature}</Typography>}
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>

            {/* Comments */}
            <Accordion sx={accordionSx}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>{sectionTitle('Comments')}</AccordionSummary>
                <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                    <Box sx={{ position: 'relative' }}>
                        <TextField multiline rows={3} size="small" fullWidth value={form.comments}
                            onChange={e => onFieldChange('comments', e.target.value)} disabled={isReadOnly} placeholder="Optional comments..." />
                        {!isReadOnly && dictationSupported && (
                            <IconButton
                                onClick={toggleCommentsDictation}
                                color={isListening ? 'error' : 'primary'}
                                size="small"
                                sx={{ position: 'absolute', right: 4, top: 4 }}
                            >
                                {isListening ? <StopIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                            </IconButton>
                        )}
                    </Box>
                </AccordionDetails>
            </Accordion>

            {/* Submit */}
            {!isReadOnly && (
                <Box sx={{ p: 1.5, pb: 3 }}>
                    <Button variant="contained" startIcon={<SendIcon />} onClick={onSubmit} fullWidth disabled={saving}
                        sx={{ py: 1.5, backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#45a049' } }}>
                        {saving ? 'Submitting...' : 'Submit'}
                    </Button>
                </Box>
            )}

            <PdfPreviewModal open={pdfPreviewOpen} onClose={() => { setPdfPreviewOpen(false); setPdfBlob(null); }}
                pdfBlob={pdfBlob} filename={pdfFilename} loading={pdfLoading} />
        </>
    );
}
